import { useState } from "react";
import useApiClient from "../useApiClient";
import { useHotelContext } from "./useHotelContext";
import useHotelSearchParams from "./useHotelSearchParams";
import { useTranslation } from "react-i18next";
import useMimir from "hooks/useMimir";

const useGetHotelAvailability = () => {
    const { api, urls } = useApiClient();
    const [error, setError] = useState(null);
    const { prepareAvailabilityPayload } = useHotelSearchParams();
    const { i18n, t } = useTranslation();
    const {
        providers,
        setAvailabilityResults,
        setAvailabilityLoading,
        setAvailabilityError,
    } = useHotelContext();
    const { mimir } = useMimir()

    const getHotelsByProvider = async (payload, provider) => {
        payload["provider"] = provider.code;
        payload["provider_id"] = provider.id;
        try {
            const res = await api.post(
                urls.hotel.availability + `?language=${i18n.language}`,
                JSON.stringify(payload)
            );
            if (
                !res.data ||
                res.data?.result.length === 0 ||
                res.data?.error ||
                (Object.keys(res.data?.result).length === 0 &&
                    res.data?.result?.constructor === Object) ||
                res.data?.result.hasOwnProperty("IBE_Errors")
            )
                return null;

            const result = Object.keys(res.data.result).map((code) => {
                const hotel = res.data.result[code];
                return {
                    ...hotel,
                    provider_id: provider.id,
                };
            });
            return {
                result,
                provider,
            };
        } catch (error) {
            console.log(error);
            return null;
        }
    };

    const getHotelAvailability = async () => {
        setAvailabilityLoading(true);
        setAvailabilityError(null);
        setAvailabilityResults(null);
        try {
            if (Array.isArray(providers) && providers.length > 0) {
                const payload = prepareAvailabilityPayload();
                let mimirData = {
                    type: "HOTEL",
                    event: "AVAILABILITY",
                    param1: JSON.stringify(payload || {}),
                    param2: [],
                    param3: [],
                    param4: `${payload.destination}` 
                };
                const results = await Promise.all(
                    providers.map(async (provider, _) => {
                        const result = await getHotelsByProvider(
                            payload,
                            provider
                        );
                        if (result) {
                            setAvailabilityResults((prevState) => {
                                const sortHotelsByPrice = (hotels) => {
                                    return hotels.sort(
                                        (a, b) =>
                                            a.minRate.total - b.minRate.total
                                    );
                                };
                                if (!prevState) {
                                    const sortedHotels = sortHotelsByPrice(
                                        result.result
                                    );
                                    return {
                                        hotels: sortedHotels,
                                        providers: [provider],
                                    };
                                }
                                const updatedHotels = [
                                    ...prevState.hotels,
                                    ...result.result,
                                ];
                                const sortedHotels =
                                    sortHotelsByPrice(updatedHotels);
                                return {
                                    ...prevState,
                                    hotels: sortedHotels,
                                    providers: [
                                        ...prevState.providers,
                                        provider,
                                    ],
                                };
                            });
                            setAvailabilityLoading(false);
                            mimirData.param2.push(`${provider?.code}:${result.result.length}`);
                        } else {
                            mimirData.param3.push(`${provider?.code}`);
                        }
                        return result;
                    })
                )
                .finally(() => {
                    mimirData["param2"] = JSON.stringify(mimirData["param2"]);
                    mimirData["param3"] = JSON.stringify(mimirData["param3"]);
                    mimir.log(mimirData);
                });
                const validResults = results.filter(
                    (result) => result !== null
                );
                if (validResults.length === 0) {
                    setAvailabilityResults(null);
                    setAvailabilityError(t("sorryNohotelsFound"));
                    console.log("Sorry, no hotel found.");
                }
            } else {
                setAvailabilityResults(null);
                setAvailabilityError("Sorry, no hotel found..");
                console.log("No providers found.");
            }
        } catch (error) {
            console.log(error);
            setAvailabilityError("An error occurred, please try again.");
        } finally {
            setAvailabilityLoading(false);
        }
    };

    return { error, setError, getHotelAvailability };
};

export default useGetHotelAvailability;
